/**
 * PatternsApi — Pattern detection, distribution, merge candidates, clusters, and emerging entities.
 *
 * Extracted from client.ts (CUR-547 god-object split). Methods-only by
 * design: prototype methods are merged onto AdminAPIClient via applyMixins
 * in client.ts, so the flat `apiClient.method()` surface is unchanged.
 * Instance state lives in BaseApiClient — do not add fields here.
 */

import { APIError as SharedAPIError, DEFAULT_RETRY_OPTIONS, buildApiUrl, processApiResponse, withRetry } from '../../../../shared/utils/api-helpers';
import type { BatchMergeResult, CandidateListParams, CandidateListResponse, ClusterListResponse, ClusterProposalListResponse, ConfidenceDistributionResponse, EmergingEntityAlert, EnrichmentProgressResponse, ImplicationMapResponse, LifecycleDistributionResponse, MergeCandidateDismissResult, MergeCandidateListResponse, PatternCluster, PatternDetailResponse, PatternEnrichmentResponse, PatternListResponse, PatternMergeResult, PatternTemporalResponse, PatternTriggersResponse, PotentialSuccessorsResponse } from '@/types/patterns';
import { API_ENDPOINTS } from '@/utils/env';
import { transformKeysToCamelCase } from '@/utils/transform-keys';

import { BaseApiClient } from './base';

export class PatternsApi extends BaseApiClient {
  // ============================================================================
  // Pattern Detection Methods
  // ============================================================================

  /**
   * Get list of patterns with optional filtering
   */
  async getPatterns(params: {
    page?: number;
    limit?: number;
    status?: string;
    patternType?: string;
    domain?: string;
    search?: string;
    minConfidence?: number;
    includeArchived?: boolean;
    isCrossDomain?: boolean;
    platform?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    sourceScope?: string;
    ungrounded?: boolean;
    missingDomainData?: boolean;
  } = {}): Promise<PatternListResponse> {
    const queryParams: Record<string, string | number | boolean> = {};
    if (params.page !== undefined) queryParams.page = params.page;
    if (params.limit !== undefined) queryParams.limit = params.limit;
    if (params.status !== undefined) queryParams.status = params.status;
    if (params.patternType !== undefined) queryParams.pattern_type = params.patternType;
    if (params.domain !== undefined) queryParams.domain = params.domain;
    if (params.search !== undefined) queryParams.search = params.search;
    if (params.minConfidence !== undefined) queryParams.min_confidence = params.minConfidence;
    if (params.includeArchived !== undefined) queryParams.include_archived = params.includeArchived;
    if (params.isCrossDomain) queryParams.domain_scope = 'cross';
    if (params.platform !== undefined) queryParams.platform = params.platform;
    if (params.sortBy !== undefined) queryParams.sort_by = params.sortBy;
    if (params.sortOrder !== undefined) queryParams.sort_order = params.sortOrder;
    if (params.sourceScope !== undefined) queryParams.source_scope = params.sourceScope;
    if (params.ungrounded) queryParams.ungrounded = true;
    if (params.missingDomainData) queryParams.missing_domain_data = true;

    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.patterns, queryParams);
    const headers = await this.resolveHeaders();

    const fetchPatterns = async () => {
      const response = await fetch(url, { headers });
      const result = await processApiResponse<unknown>(response);

      if (result.error) {
        throw new SharedAPIError(result.status, result.error.message, result.error.code);
      }

      if (!result.data) {
        throw new SharedAPIError(500, 'No data returned from patterns endpoint');
      }

      // Transform snake_case keys from backend to camelCase for frontend
      return transformKeysToCamelCase<PatternListResponse>(result.data);
    };

    return withRetry(fetchPatterns, DEFAULT_RETRY_OPTIONS);
  }

  /**
   * CUR-464 — admin-only list of pattern pairs flagged by the daily
   * merge-candidate scanner. Read-only surfacing; merges are performed via
   * the existing POST /api/patterns/merge endpoint.
   */
  async getMergeCandidates(): Promise<MergeCandidateListResponse> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.patternMergeCandidates);
    const headers = await this.resolveHeaders();

    const response = await fetch(url, { headers });
    const result = await processApiResponse<unknown>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    if (!result.data) {
      throw new SharedAPIError(500, 'No data returned from merge-candidates endpoint');
    }

    return transformKeysToCamelCase<MergeCandidateListResponse>(result.data);
  }

  /**
   * CUR-948 — merge a duplicate pattern into a survivor. Destructive: the
   * absorbed pattern is archived; entities/triggers/domains union onto the
   * survivor. Super-admin only (enforced server-side).
   */
  async mergePatterns(survivorId: string, absorbedId: string): Promise<PatternMergeResult> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.patternMerge(survivorId));
    const headers = await this.resolveHeaders();
    const response = await fetch(url, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ absorbed_pattern_id: absorbedId }),
    });
    const result = await processApiResponse<unknown>(response);
    if (result.error) throw new SharedAPIError(result.status, result.error.message, result.error.code);
    if (!result.data) throw new SharedAPIError(500, 'No data returned from merge endpoint');
    return transformKeysToCamelCase<PatternMergeResult>(result.data);
  }

  /**
   * CUR-949 — dismiss a merge-candidate pair as not-a-duplicate. The daily
   * scanner carries the dismissal forward so the pair stays out of the queue.
   */
  async dismissMergeCandidate(patternAId: string, patternBId: string): Promise<MergeCandidateDismissResult> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.patternMergeCandidateDismiss);
    const headers = await this.resolveHeaders();
    const response = await fetch(url, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ pattern_a_id: patternAId, pattern_b_id: patternBId }),
    });
    const result = await processApiResponse<unknown>(response);
    if (result.error) throw new SharedAPIError(result.status, result.error.message, result.error.code);
    if (!result.data) throw new SharedAPIError(500, 'No data returned from dismiss endpoint');
    return transformKeysToCamelCase<MergeCandidateDismissResult>(result.data);
  }

  /**
   * CUR-950 — batch-merge high-confidence duplicate pairs. Each pair is applied
   * independently server-side; the response reports merged vs skipped.
   */
  async batchMergePatterns(
    merges: { survivorId: string; absorbedId: string }[],
  ): Promise<BatchMergeResult> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.patternMergeCandidateBatchMerge);
    const headers = await this.resolveHeaders();
    const response = await fetch(url, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        merges: merges.map((m) => ({ survivor_id: m.survivorId, absorbed_id: m.absorbedId })),
      }),
    });
    const result = await processApiResponse<unknown>(response);
    if (result.error) throw new SharedAPIError(result.status, result.error.message, result.error.code);
    if (!result.data) throw new SharedAPIError(500, 'No data returned from batch-merge endpoint');
    return transformKeysToCamelCase<BatchMergeResult>(result.data);
  }

  /** Plan A — formed clusters with members. */
  async getClusters(): Promise<ClusterListResponse> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.patternClusters);
    const headers = await this.resolveHeaders();
    const response = await fetch(url, { headers });
    const result = await processApiResponse<unknown>(response);
    if (result.error) throw new SharedAPIError(result.status, result.error.message, result.error.code);
    if (!result.data) throw new SharedAPIError(500, 'No data returned from clusters endpoint');
    return transformKeysToCamelCase<ClusterListResponse>(result.data);
  }

  /** CUR-951 — fetch one cluster with members + audit for the detail view. */
  async getCluster(clusterId: string): Promise<PatternCluster> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.patternCluster(clusterId));
    const headers = await this.resolveHeaders();
    const response = await fetch(url, { headers });
    const result = await processApiResponse<unknown>(response);
    if (result.error) throw new SharedAPIError(result.status, result.error.message, result.error.code);
    if (!result.data) throw new SharedAPIError(404, 'Cluster not found');
    return transformKeysToCamelCase<PatternCluster>(result.data);
  }

  /** Plan A — live human-lane proposals. */
  async getClusterProposals(): Promise<ClusterProposalListResponse> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.patternClusterProposals);
    const headers = await this.resolveHeaders();
    const response = await fetch(url, { headers });
    const result = await processApiResponse<unknown>(response);
    if (result.error) throw new SharedAPIError(result.status, result.error.message, result.error.code);
    if (!result.data) throw new SharedAPIError(500, 'No data returned from proposals endpoint');
    return transformKeysToCamelCase<ClusterProposalListResponse>(result.data);
  }

  /** Plan A — confirm a proposal into a manual cluster. */
  async confirmCluster(body: { patternIds: string[]; title?: string; narrative?: string }): Promise<PatternCluster> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.patternClusters);
    const headers = await this.resolveHeaders();
    const response = await fetch(url, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ pattern_ids: body.patternIds, title: body.title, narrative: body.narrative }),
    });
    const result = await processApiResponse<unknown>(response);
    if (result.error) throw new SharedAPIError(result.status, result.error.message, result.error.code);
    if (!result.data) throw new SharedAPIError(500, 'No data returned from confirm cluster');
    return transformKeysToCamelCase<PatternCluster>(result.data);
  }

  /** Plan A — edit title/narrative. */
  async updateCluster(clusterId: string, body: { title?: string; narrative?: string }): Promise<PatternCluster> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.patternCluster(clusterId));
    const headers = await this.resolveHeaders();
    const response = await fetch(url, {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const result = await processApiResponse<unknown>(response);
    if (result.error) throw new SharedAPIError(result.status, result.error.message, result.error.code);
    if (!result.data) throw new SharedAPIError(500, 'No data returned from update cluster');
    return transformKeysToCamelCase<PatternCluster>(result.data);
  }

  /** Plan A — dismiss/disband a cluster. */
  async dismissCluster(clusterId: string): Promise<void> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.patternCluster(clusterId));
    const headers = await this.resolveHeaders();
    const response = await fetch(url, { method: 'DELETE', headers });
    if (!response.ok && response.status !== 204) {
      const result = await processApiResponse<unknown>(response);
      throw new SharedAPIError(result.status, result.error?.message ?? 'Failed to dismiss cluster', result.error?.code);
    }
  }

  /** Plan A — remove a member from a cluster. */
  async removeClusterMember(clusterId: string, patternId: string): Promise<void> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.patternClusterMember(clusterId, patternId));
    const headers = await this.resolveHeaders();
    const response = await fetch(url, { method: 'DELETE', headers });
    if (!response.ok && response.status !== 204) {
      const result = await processApiResponse<unknown>(response);
      throw new SharedAPIError(result.status, result.error?.message ?? 'Failed to remove member', result.error?.code);
    }
  }

  /**
   * Get a single pattern by ID with full details
   */
  async getPattern(patternId: string): Promise<PatternDetailResponse> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.pattern(patternId));
    const headers = await this.resolveHeaders();

    const response = await fetch(url, { headers });
    const result = await processApiResponse<unknown>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    if (!result.data) {
      throw new SharedAPIError(404, 'Pattern not found');
    }

    // Transform snake_case keys from backend to camelCase for frontend
    return transformKeysToCamelCase<PatternDetailResponse>(result.data);
  }

  /**
   * Get temporal analysis for a pattern (timeline, velocity history, forecasts)
   */
  async getPatternTemporal(patternId: string): Promise<PatternTemporalResponse> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.patternTemporal(patternId));
    const headers = await this.resolveHeaders();

    const response = await fetch(url, { headers });
    const result = await processApiResponse<unknown>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    if (!result.data) {
      throw new SharedAPIError(404, 'Pattern temporal data not found');
    }

    // Transform snake_case keys from backend to camelCase for frontend
    return transformKeysToCamelCase<PatternTemporalResponse>(result.data);
  }

  /**
   * Get trigger hypotheses for a pattern
   */
  async getPatternTriggers(patternId: string): Promise<PatternTriggersResponse> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.patternTriggers(patternId));
    const headers = await this.resolveHeaders();

    const response = await fetch(url, { headers });
    const result = await processApiResponse<unknown>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    if (!result.data) {
      throw new SharedAPIError(404, 'Pattern triggers not found');
    }

    // Transform snake_case keys from backend to camelCase for frontend
    return transformKeysToCamelCase<PatternTriggersResponse>(result.data);
  }

  /**
   * Trigger enrichment for a pattern (re-analyze with latest data)
   */
  async enrichPattern(
    patternId: string,
    enrichmentTypes: string[] = ['forecast', 'connections', 'entity_momentum']
  ): Promise<PatternEnrichmentResponse> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.patternEnrich(patternId));
    const headers = await this.resolveHeaders();

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ enrichment_types: enrichmentTypes }),
    });

    const result = await processApiResponse<unknown>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    if (!result.data) {
      throw new SharedAPIError(500, 'No data returned from enrichment endpoint');
    }

    // Transform snake_case keys from backend to camelCase for frontend
    return transformKeysToCamelCase<PatternEnrichmentResponse>(result.data);
  }

  /**
   * Get enrichment progress for a running task
   */
  async getEnrichmentProgress(taskId: string): Promise<EnrichmentProgressResponse> {
    const url = buildApiUrl(this.baseUrl, `/api/patterns/enrich/progress/${taskId}`);
    const headers = await this.resolveHeaders();

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    const result = await processApiResponse<unknown>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    if (!result.data) {
      throw new SharedAPIError(500, 'No data returned from progress endpoint');
    }

    return transformKeysToCamelCase<EnrichmentProgressResponse>(result.data);
  }

  /**
   * Get potential successors for a declining/normalizing pattern
   */
  async getPatternPotentialSuccessors(patternId: string): Promise<PotentialSuccessorsResponse> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.patternPotentialSuccessors(patternId));
    const headers = await this.resolveHeaders();

    const response = await fetch(url, { headers });
    const result = await processApiResponse<unknown>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    if (!result.data) {
      throw new SharedAPIError(404, 'Potential successors data not found');
    }

    return transformKeysToCamelCase<PotentialSuccessorsResponse>(result.data);
  }

  /**
   * Get candidate patterns (bubbling up but not yet tracked)
   *
   * These are signals detected by the pattern detection system that don't
   * meet the threshold for being tracked as full patterns.
   */
  async getCandidatePatterns(
    params: CandidateListParams = {}
  ): Promise<CandidateListResponse> {
    const queryParams: Record<string, string> = {};

    if (params.timeWindow) {
      queryParams.time_window = params.timeWindow;
    }
    if (params.domain) {
      queryParams.domain = params.domain;
    }
    if (params.platform) {
      queryParams.platform = params.platform;
    }
    if (params.limit) {
      queryParams.limit = String(params.limit);
    }
    if (params.sourceScope) {
      queryParams.source_scope = params.sourceScope;
    }

    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.patternCandidates, queryParams);
    const headers = await this.resolveHeaders();

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    const result = await processApiResponse<unknown>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    if (!result.data) {
      throw new SharedAPIError(500, 'No data returned from candidates endpoint');
    }

    // Transform snake_case keys from backend to camelCase for frontend
    return transformKeysToCamelCase<CandidateListResponse>(result.data);
  }

  // ============================================================================
  // Pattern Distribution & Emerging Entity Methods
  // ============================================================================

  async getConfidenceDistribution(): Promise<ConfidenceDistributionResponse> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.patternConfidenceDistribution);
    const headers = await this.resolveHeaders();
    const response = await fetch(url, { method: 'GET', headers });
    const result = await processApiResponse<unknown>(response);
    if (result.error) throw new SharedAPIError(result.status, result.error.message, result.error.code);
    if (!result.data) throw new SharedAPIError(500, 'No data returned');
    return transformKeysToCamelCase<ConfidenceDistributionResponse>(result.data);
  }

  async getLifecycleDistribution(): Promise<LifecycleDistributionResponse> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.patternLifecycleDistribution);
    const headers = await this.resolveHeaders();
    const response = await fetch(url, { method: 'GET', headers });
    const result = await processApiResponse<unknown>(response);
    if (result.error) throw new SharedAPIError(result.status, result.error.message, result.error.code);
    if (!result.data) throw new SharedAPIError(500, 'No data returned');
    return transformKeysToCamelCase<LifecycleDistributionResponse>(result.data);
  }

  async getEmergingEntities(): Promise<EmergingEntityAlert[]> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.emergingEntities);
    const headers = await this.resolveHeaders();
    const response = await fetch(url, { method: 'GET', headers });
    const result = await processApiResponse<unknown>(response);
    if (result.error) throw new SharedAPIError(result.status, result.error.message, result.error.code);
    if (!result.data) return [];
    return transformKeysToCamelCase<EmergingEntityAlert[]>(result.data);
  }

  async emergingEntityAction(alertId: string, action: 'promote' | 'dismiss'): Promise<void> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.emergingEntityAction(alertId, action));
    const headers = await this.resolveHeaders();
    const response = await fetch(url, { method: 'POST', headers });
    const result = await processApiResponse<unknown>(response);
    if (result.error) throw new SharedAPIError(result.status, result.error.message, result.error.code);
  }

  async getImplicationMap(patternId: string): Promise<ImplicationMapResponse | null> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.patternImplications(patternId));
    const headers = await this.resolveHeaders();
    const response = await fetch(url, { method: 'GET', headers });
    const result = await processApiResponse<unknown>(response);
    if (result.error) throw new SharedAPIError(result.status, result.error.message, result.error.code);
    if (!result.data) return null;
    return transformKeysToCamelCase<ImplicationMapResponse>(result.data);
  }

  async generateImplicationMap(patternId: string): Promise<ImplicationMapResponse> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.patternImplicationsGenerate(patternId));
    const headers = await this.resolveHeaders();
    const response = await fetch(url, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: '{}',
    });
    const result = await processApiResponse<unknown>(response);
    if (result.error) throw new SharedAPIError(result.status, result.error.message, result.error.code);
    if (!result.data) throw new SharedAPIError(500, 'No data returned');
    return transformKeysToCamelCase<ImplicationMapResponse>(result.data);
  }

}
